<template lang="pug">
  .study-individual(:data-header-opened='isHeaderOpened', :data-screen-ratio='screenRatio', :data-vertical='isVertical')
    //- ヘッダー部
    study-header.study-individual__header(:returnEvent='returnButtonEvent', :isFirstTime='isFirstTime', @change-toggle='changeHeaderToggle')
      template(#userUidTitle)
        slot(name='userUidTitle')
      template(#userNameTitle)
        slot(name='userNameTitle')
      template(#returnText)
        slot(name='returnButtonText')
    //- コンテンツ部
    .study-individual__content(@click='hideStudyMenu')
      //-  ナビゲーション部
      .study-individual__content__navigation(@click.stop)
        study-navigation.study-individual__content__navigation__body(
          :studyTitle='studyTitle',
          :currentTime='currentTime',
          :remainingTime='remainingTime',
          :isVertical='isVertical',
          :showNextButton='showNextButton',
          :showPrevButton='showPrevButton',
          :nextButtonEvent='nextButtonEvent',
          :prevButtonEvent='prevButtonEvent',
          :menuButtonEvent='toggleStudyMenu'
        )
          template(#currentTimeLabel)
            slot(name='currentTimeLabel')
          template(#remainingTimeLabel)
            slot(name='remainingTimeLabel')
      //- 問題解答部
      .study-individual__content__body(v-if='currentProblem && currentQuestion')
        //- 問題
        component.study-individual__content__body__question(
          :is='questionComponent',
          ref='questionComponent',
          :isChangingProblem='isChangingProblem',
          :problemNumber='currentProblem.problemNumber',
          :questionNumberText='currentQuestion.questionNumberText',
          :imageUrls='imageUrls',
          :audioViewSetting='audioViewSetting',
          :audioUrl='audioInfo.url',
          :audioPlayCount='audioInfo.count',
          :audioCommentImageUrl='audioCommentImageUrl',
          :audioStartedEvent='audioStartedEvent',
          :questionDrawingData='questionMemo.drawingData',
          :drawEndEvent='drawEndEvent'
        )
          template(#problemNumberBeforeLabel)
            slot(name='problemNumberBeforeLabel')
          template(#problemNumberAfterLabel)
            slot(name='problemNumberAfterLabel')
        //- 解答
        component.study-individual__content__body__answer(
          :is='answerComponent',
          :isChangingProblem='isChangingProblem',
          :problemNumber='currentProblem.problemNumber',
          :questionNumberText='currentQuestion.questionNumberText',
          :questionId='currentQuestion.id',
          :checkedQuestionIds='checkedQuestionIds',
          :indicativeText='currentQuestion.indicativeText',
          :answerFormat='currentQuestion.answerFormat',
          :questionDatas='currentQuestion.questionDatas',
          :questionChoiceData='currentQuestion.questionChoiceData',
          :mathKeyboardName='mathKeyboardName',
          :showNextButton='showNextButton',
          :nextEvent='nextButtonEvent',
          :completeEvent='completeButtonEvent',
          @check-question='checkQuestion'
        )
          template(#problemNumberBeforeLabel)
            slot(name='problemNumberBeforeLabel')
          template(#problemNumberAfterLabel)
            slot(name='problemNumberAfterLabel')
          template(#questionCheckboxLabel)
            slot(name='questionCheckboxLabel')
          template(#tegakiResultTitle)
            slot(name='tegakiResultTitle')
          template(#tegakiRecognizeButtonLabel)
            slot(name='tegakiRecognizeButtonLabel')
          template(#nextButtonLabel)
            slot(name='answerNextButtonLabel')
          template(#completeButtonLabel)
            slot(:name='answerCompleteButtonLabel')
        //- 学習メニュー
        study-menu.study-individual__content__body__menu(
          ref='studyMenu',
          :problems='problems',
          :inputtedQuestionIds='inputtedQuestionIds',
          :checkedQuestionIds='checkedQuestionIds',
          :selectedProblemId='currentProblem.id',
          :selectedQuestionId='currentQuestion.id',
          :selectQuestionEvent='selectQuestionEvent',
          :completeButtonEvent='completeButtonEvent'
        )
          template(#problemNumberBeforeLabel)
            slot(name='menuProblemNumberBeforeLabel')
          template(#problemNumberAfterLabel)
            slot(name='menuProblemNumberAfterLabel')
          template(#completeButtonLabel)
            slot(:name='menuCompleteButtonLabel')
    //- 完了確認モーダル
    modal-study-confirm.study-individual__modal(
      ref='modalStudyConfirm',
      :notAnsweredNumbers='notAnsweredNumbers',
      :submitEvent='doStudyComplete'
    )
      template(#studyConfirmTitle)
        slot(name='studyConfirmTitle')
      template(#notAnsweredLabel)
        slot(name='notAnsweredLabel')
      template(#studyConfirmText1)
        slot(:name='studyConfirmText1')
      template(#studyConfirmText2)
        slot(name='studyConfirmText2')
      template(#submitText)
        slot(name='submitText')
      template(#hideText)
        slot(name='hideText')
    //- 中断確認モーダル
    modal-study-confirm.study-individual__modal(ref='modalStudySuspend', :submitEvent='moveReturnEvent')
      template(#studyConfirmTitle)
        slot(name='studySuspendTitle')
      template(#studyConfirmText1)
        slot(name='studySuspendText1')
      template(#studyConfirmText2)
        slot(name='studySuspendText2')
      template(#submitText)
        slot(name='submitText')
      template(#hideText)
        slot(name='hideText')
    //- 完了モーダル
    modal-study-complete.study-individual__modal(ref='modalStudyComplete', :submitEvent='studyEndEvent')
      template(#studyCompleteTitle)
        slot(name='studyCompleteTitle')
      template(#submitText)
        slot(name='completeSubmitText')
  </template>

<script lang="ts">
import { Component, Prop, Ref } from 'vue-property-decorator'
import StudyHeader from '@/components/organisms/student/StudyHeader.vue'
import StudyNavigation from '@/components/organisms/student/StudyNavigation.vue'
import StudyQuestionHorizontal from '@/components/organisms/student/StudyQuestionHorizontal.vue'
import StudyQuestionVertical from '@/components/organisms/student/StudyQuestionVertical.vue'
import StudyAnswerHorizontal from '@/components/organisms/student/StudyAnswerHorizontal.vue'
import StudyAnswerVertical from '@/components/organisms/student/StudyAnswerVertical.vue'
import StudyMenu from '@/components/organisms/student/StudyMenu.vue'
import ModalStudyConfirm from '@/components/organisms/student/ModalStudyConfirm.vue'
import ModalStudyComplete from '@/components/organisms/student/ModalStudyComplete.vue'
import { StudyProblem, StudyQuestion, StudyAudioInfoType, StudyQuestionMemoType } from '@/models/studyModel'
import { AudioViewSetting } from '@/const/AudioViewTypes'
import * as domtoimage from 'dom-to-image-more'
import { Mixins } from 'vue-property-decorator'
import DrillApi from '@/mixins/api/DrillApi'

/**
 * テスト画面（問題解答表示）
 */
@Component({
  components: {
    StudyHeader,
    StudyNavigation,
    StudyQuestionHorizontal,
    StudyQuestionVertical,
    StudyAnswerHorizontal,
    StudyAnswerVertical,
    StudyMenu,
    ModalStudyConfirm,
    ModalStudyComplete,
  },
})
export default class StudyIndividual extends Mixins(DrillApi) {
  // ヘッダーの開閉情報
  private isHeaderOpened = false

  // 学習タイトル
  @Prop()
  studyTitle!: string

  // 実施時間
  @Prop()
  currentTime!: string

  // 残り時間
  @Prop()
  remainingTime!: string

  // 問題切替中フラグ(主に問題を切り替えるタイミングで、コンポーネントを再描画する場合に用いる)
  @Prop({ default: false })
  isChangingProblem!: boolean

  // 縦書きフラグ
  @Prop()
  isVertical!: boolean

  // 最終大問フラグ
  @Prop()
  isLastProblem!: boolean

  // 初回フラグ
  @Prop()
  isFirstTime!: Boolean

  // 次へボタン押下時イベント
  @Prop()
  nextButtonEvent!: Function

  // 前へボタン押下時イベント
  @Prop()
  prevButtonEvent!: Function

  // 音声コメント画像URL
  @Prop()
  audioCommentImageUrl!: string

  // 音声表示設定
  @Prop()
  audioViewSetting!: AudioViewSetting

  // 音声情報
  @Prop()
  audioInfo!: StudyAudioInfoType

  // 音声再生時イベント
  @Prop()
  audioStartedEvent!: Function

  // 描画終了時イベント
  @Prop()
  drawEndEvent!: Function

  // メモ画像Src
  @Prop()
  questionMemo!: StudyQuestionMemoType

  // 全問題
  @Prop()
  problems!: StudyProblem[]

  // 選択中の問題
  @Prop()
  currentProblem!: StudyProblem

  // 選択中の設問
  @Prop()
  currentQuestion!: StudyQuestion

  // 数学キーボード名
  @Prop()
  mathKeyboardName!: string

  // 設問選択時イベント
  @Prop()
  selectQuestionEvent!: Function

  // 未入力問題番号
  @Prop()
  notAnsweredNumbers!: string[]

  // 学習完了確認時イベント
  @Prop()
  studyConfirmEvent!: Function

  // 学習完了時イベント
  @Prop()
  studyCompleteEvent!: Function

  // 学習終了時イベント
  @Prop()
  studyEndEvent!: Function

  // 一覧に戻るイベント
  @Prop()
  moveReturnEvent!: Function

  // 問題表示エリアの割合
  @Prop({ default: 1.5 })
  screenRatio?: number

  @Ref() studyMenu!: StudyMenu
  @Ref() modalStudyConfirm!: ModalStudyConfirm
  @Ref() modalStudySuspend!: ModalStudyConfirm
  @Ref() modalStudyComplete!: ModalStudyComplete
  @Ref('questionComponent') questionComponentRef!: StudyQuestionHorizontal | StudyQuestionVertical

  // === 選択値を保持する変数群
  // 入力済み設問ID
  public inputtedQuestionIds: number[] = []
  // チェック済み設問ID
  public checkedQuestionIds: number[] = []
  // キャプチャー用の画像データを保持
  private captureImageData: Map<string, string> = new Map()
  // 結果ドリルID
  private resultDrillId: number | null = null
  // 縦書き/横書きコンポーネント名取得
  private get questionComponent(): string {
    return this.isVertical ? 'StudyQuestionVertical' : 'StudyQuestionHorizontal'
  }
  private get answerComponent(): string {
    return this.isVertical ? 'StudyAnswerVertical' : 'StudyAnswerHorizontal'
  }

  // 学習完了ボタンラベル
  private get answerCompleteButtonLabel(): string {
    return this.isLastProblem ? 'answerCompleteButtonLabel' : 'answerCompleteProblemButtonLabel'
  }

  // 学習メニュー完了ボタンラベル
  private get menuCompleteButtonLabel(): string {
    return this.isLastProblem ? 'menuCompleteButtonLabel' : 'menuCompleteProblemButtonLabel'
  }

  // 完了モーダルテキスト
  private get studyConfirmText1(): string {
    return this.isLastProblem ? 'studyConfirmText1a' : 'studyConfirmText1b'
  }

  // 次へボタン表示フラグ
  private get showNextButton(): boolean {
    // 選択中の問題が全問題中最後の問題でなければ表示
    const lastProblem = this.problems[this.problems.length - 1]
    return this.currentQuestion !== lastProblem.questions[lastProblem.questions.length - 1]
  }

  // 前へボタン表示フラグ
  private get showPrevButton(): boolean {
    // 選択中の問題が全問題中最初の問題でなければ表示
    return this.currentQuestion !== this.problems[0].questions[0]
  }

  // 問題に表示する画像URL
  private get imageUrls(): string[] {
    // 設問画像URL群に問題画像URL群を連結
    return this.currentProblem.imageUrls.concat(this.currentQuestion.imageUrls)
  }

  // ヘッダーの開閉情報を更新
  private changeHeaderToggle(value: boolean) {
    this.isHeaderOpened = value
  }

  // チェック済み設問IDを更新
  private checkQuestion(value: number[]) {
    this.checkedQuestionIds = value
  }

  // 学習メニューを閉じる
  private hideStudyMenu() {
    this.studyMenu.hide()
  }

  // 学習メニュー開閉
  private toggleStudyMenu() {
    this.studyMenu.toggle()
  }

  // 学習メニュー及び学習画面の終了ボタン押下時の処理
  private completeButtonEvent() {
    // 完了確認時イベントを実行
    this.studyConfirmEvent()
    // 確認モーダルを表示
    this.modalStudyConfirm.show()
  }

  // ヘッダーの戻るボタン押下時の処理
  private returnButtonEvent() {
    // 中断確認モーダルを開く
    this.modalStudySuspend.show()
  }

  private async doStudyComplete() {
    try {
      await this.studyCompleteEvent()
      this.modalStudyConfirm.hide()

      if (this.isLastProblem) this.modalStudyComplete.show()
    } catch (error) {
      console.error('[doStudyComplete] 提出処理でエラーが発生しました:', error)
    }
  }

  /**
   * 親コンポーネントのための結果ドリルIDを設定
   */
  public setResultDrillId(id: number): void {
    this.resultDrillId = id
  }

  /**
   * 画像読み込み
   */
  public async loadImagesAsync(problem: StudyProblem, question?: StudyQuestion): Promise<void> {
    try {
      const urls: string[] = []

      if (problem.imageUrls.length > 0) {
        urls.push(...problem.imageUrls)
      }
      // 設問の画像URL
      if (question?.imageUrls?.length) {
        urls.push(...question.imageUrls)
      }
      // 選択肢の画像URL
      if (question?.questionChoiceData?.choiceItems) {
        question.questionChoiceData.choiceItems.forEach((choiceItem) => {
          if (choiceItem.choiceImage && choiceItem.choiceImage.length > 0) {
            urls.push(choiceItem.choiceImage)
          }
        })
      }

      const uniqueUrls = [...new Set(urls)]

      if (uniqueUrls.length === 0) {
        return
      }

      // API経由で画像を読み込む
      if (this.resultDrillId === null) {
        return
      }
      const result = await this.loadImagesApi(this.resultDrillId, uniqueUrls)

      if (result.image_data) {
        Object.entries(result.image_data).forEach(([url, base64Data]) => {
          this.captureImageData.set(url, base64Data)
        })
      }

      this.updateImagesOnScreen()
    } catch (error) {
      console.error('[loadImagesAsync] 画像読み込みエラー:', error)
    }
  }

  /**
   * 画面に表示されている画像をbase64で更新
   */
  private updateImagesOnScreen(): void {
    if (this.captureImageData.size === 0) return

    const images = this.$el.querySelectorAll<HTMLImageElement>('img')

    images.forEach((img) => {
      if (!img.src.includes('.s3.') && !img.src.includes('amazonaws.com')) {
        return
      }

      const baseUrl = img.src.split('?')[0]

      // キャッシュから探す
      let base64Data: string | undefined
      for (const [keyUrl, data] of this.captureImageData.entries()) {
        const keyBase = keyUrl.split('?')[0]
        if (keyBase === baseUrl) {
          base64Data = data
          break
        }
      }

      if (base64Data && !img.src.startsWith('data:')) {
        img.src = base64Data
      }
    })
  }

  /**
   * プレースホルダー画像を生成
   */
  private createPlaceholderImage(width: number, height: number): string {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return ''

    canvas.width = width
    canvas.height = height

    ctx.fillStyle = '#f5f5f5'
    ctx.fillRect(0, 0, width, height)
    ctx.strokeStyle = '#ddd'
    ctx.lineWidth = 2
    ctx.strokeRect(1, 1, width - 2, height - 2)

    return canvas.toDataURL('image/png', 1.0)
  }

  /**
   * S3画像URLかどうかを判定
   */
  private isS3ImageUrl(url: string): boolean {
    return url.includes('.s3.') || url.includes('amazonaws.com')
  }

  /**
   * select要素のスタイリングを調整（キャプチャー用）
   */
  private fixSelectElements(clonedElement: HTMLElement): void {
    const selectElements = clonedElement.querySelectorAll('select')

    selectElements.forEach((select) => {
      try {
        select.style.appearance = 'none'
        ;(select.style as any).webkitAppearance = 'none'
        ;(select.style as any).mozAppearance = 'none'
        select.style.backgroundImage = 'none'
        select.style.border = '1px solid #ccc'
        select.style.borderRadius = '4px'
        select.style.padding = '8px 12px'
        select.style.fontSize = '14px'
        select.style.backgroundColor = '#fff'
        select.style.color = '#333'
        select.style.width = '100%'
        select.style.height = 'auto'
        select.style.minHeight = '36px'

        select.blur()

        const selectedOption = select.options[select.selectedIndex]
        if (selectedOption && selectedOption.value) {
          select.style.position = 'relative'
          select.setAttribute('data-selected-text', selectedOption.text || selectedOption.value)

          const selectStyle = `
            select[data-selected-text]::after {
              content: attr(data-selected-text);
              position: absolute;
              left: 12px;
              top: 50%;
              transform: translateY(-50%);
              pointer-events: none;
              color: #333;
              background: transparent;
            }
          `

          const styleElement = document.createElement('style')
          styleElement.textContent = selectStyle
          clonedElement.appendChild(styleElement)
        }

        select.style.outline = 'none'
        select.style.boxShadow = 'none'
      } catch (error) {
        console.warn('[fixSelectElements] select要素の調整でエラー:', error)
      }
    })
  }

  /**
   * 画像をBase64データに置き換え
   */
  private async replaceImagesWithBase64(clonedElement: HTMLElement): Promise<Map<HTMLImageElement, string>> {
    const originalSources = new Map<HTMLImageElement, string>()
    const images = clonedElement.querySelectorAll<HTMLImageElement>('img')

    for (const img of images) {
      const imgSrc = img.src

      if (!this.isS3ImageUrl(imgSrc)) {
        continue
      }

      const baseUrl = imgSrc.split('?')[0]
      let base64Data: string | undefined

      // キャッシュから探す
      for (const [keyUrl, data] of this.captureImageData.entries()) {
        const keyBase = keyUrl.split('?')[0]
        if (keyBase === baseUrl) {
          base64Data = data
          break
        }
      }

      if (base64Data) {
        originalSources.set(img, imgSrc)
        img.src = base64Data
      } else {
        const width = img.naturalWidth || img.width || 300
        const height = img.naturalHeight || img.height || 200
        const placeholder = this.createPlaceholderImage(width, height)
        originalSources.set(img, imgSrc)
        img.src = placeholder
      }
    }

    return originalSources
  }

  /**
   * キャンバスの内容を元の要素からクローン要素にコピー
   */
  private async copyCanvasContents(originalElement: HTMLElement, clonedElement: HTMLElement): Promise<void> {
    const originalCanvas = originalElement.querySelector('canvas.pen-canvas__canvas') as HTMLCanvasElement | null
    const clonedCanvas = clonedElement.querySelector('canvas.pen-canvas__canvas') as HTMLCanvasElement | null

    // originalCanvasとclonedCanvas両方存在する場合のみ処理する
    if (!originalCanvas || !clonedCanvas) return

    // 同じインデックスのキャンバス同士で内容をコピー
    try {
      const originalParent = originalCanvas.parentElement as HTMLElement
      const clonedParent = clonedCanvas.parentElement as HTMLElement

      if (!originalParent || !clonedParent) return
      const clonedContext = clonedCanvas.getContext('2d')

      if (clonedContext) {
        clonedContext.imageSmoothingEnabled = true
        clonedContext.imageSmoothingQuality = 'high'
        clonedContext.clearRect(0, 0, clonedCanvas.width, clonedCanvas.height)

        const scaleX = clonedCanvas.width / originalCanvas.width
        const scaleY = clonedCanvas.height / originalCanvas.height

        clonedContext.save()
        clonedContext.scale(scaleX, scaleY)

        clonedContext.drawImage(originalCanvas, 0, 0)
        clonedContext.restore()
      }
    } catch (error) {
      console.warn(`[copyCanvasContents] キャンバスコピーエラー`, error)
    }
  }

  /**
   * 画面キャプチャを取得（public wrapper）
   */
  public async captureScreen(): Promise<string | null> {
    const element = document.querySelector('.study-individual') as HTMLElement
    if (!element) {
      console.error('[captureScreen] キャプチャ対象の要素が見つかりません')
      return null
    }
    return this.doCapture(element, { replaceBase64Images: true })
  }

  /**
   * 画面の指定部分をキャプチャ（内部実装）
   */
  private async doCapture(
    element: HTMLElement,
    options: { replaceBase64Images?: boolean } = {}
  ): Promise<string | null> {
    try {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'

      const clonedElement = element.cloneNode(true) as HTMLElement
      clonedElement.style.position = 'absolute'
      clonedElement.style.top = '0'
      clonedElement.style.left = '-9999px'
      clonedElement.style.zIndex = '-1'

      const FIXED_WIDTH = 1600
      const FIXED_HEIGHT = Math.round((FIXED_WIDTH * 9) / 14)

      clonedElement.style.width = `${FIXED_WIDTH}px`
      clonedElement.style.minWidth = `${FIXED_WIDTH}px`
      clonedElement.style.maxWidth = `${FIXED_WIDTH}px`
      clonedElement.style.height = `${FIXED_HEIGHT}px`
      clonedElement.style.minHeight = `${FIXED_HEIGHT}px`
      clonedElement.style.maxHeight = `${FIXED_HEIGHT}px`
      clonedElement.style.padding = '0 10px'
      clonedElement.style.boxSizing = 'border-box'
      clonedElement.style.background = 'white'
      clonedElement.style.overflow = 'hidden'
      clonedElement.style.position = 'relative'

      document.body.appendChild(clonedElement)

      const menuButton = clonedElement.querySelector('.button-base.button-menu.study-navigation__control__button-menu')
      if (menuButton) {
        ;(menuButton as HTMLElement).style.display = 'none'
      }

      const studyMenu = clonedElement.querySelector('.study-menu.study-individual__content__body__menu')
      if (studyMenu) {
        ;(studyMenu as HTMLElement).style.display = 'none'
      }

      // ダイアログを非表示にする
      const modals = clonedElement.querySelectorAll('.vm--modal, .vm--overlay')
      modals.forEach((modal) => {
        ;(modal as HTMLElement).style.display = 'none'
      })

      this.fixSelectElements(clonedElement)

      if (options.replaceBase64Images && this.captureImageData.size > 0) {
        await this.replaceImagesWithBase64(clonedElement)
      }

      // キャンバスの内容をコピー
      await this.copyCanvasContents(element, clonedElement)

      await new Promise((resolve) => setTimeout(resolve, 500))

      let dataUrl = await domtoimage.toPng(clonedElement, {
        width: FIXED_WIDTH,
        height: FIXED_HEIGHT,
        bgcolor: 'white',
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
        },
      })

      // Safariブラウザ用dataUrl生成
      if (this.isSafari()) {
        dataUrl = await this.buildDataUrlWithRetry(clonedElement)
      }
      document.body.style.overflow = 'auto'

      return dataUrl
    } catch (error) {
      console.error('[doCapture] キャプチャに失敗しました', error)
      return null
    } finally {
      const clonedElement = document.querySelector('[style*="left: -9999px"]')
      if (clonedElement && document.body.contains(clonedElement)) {
        document.body.removeChild(clonedElement)
      }
    }
  }

  /**
   * 手書きメモキャプチャ
   */
  private captureHandwriting(): string | null {
    try {
      if (!this.questionComponentRef) {
        return null
      }

      // 問題コンポーネントからPenCanvasにアクセス
      const penCanvas = (this.questionComponentRef as any).penCanvas
      if (!penCanvas) {
        return null
      }

      return penCanvas.captureDrawing()
    } catch (error) {
      console.error('[captureHandwriting] 手書きメモキャプチャエラー', error)
      return null
    }
  }

  /**
   * 問題画像をキャプチャ
   */
  private captureQuestionImages(): string[] {
    const images = document.querySelectorAll<HTMLImageElement>(
      '.image-base.study-question-horizontal__content__question__image, .image-base.study-question-vertical__content__question__image'
    )
    const captures: string[] = []

    for (const img of images) {
      if (img.src) {
        // キャッシュからBase64データを取得
        let base64Data: string | undefined
        for (const [keyUrl, data] of this.captureImageData.entries()) {
          const keyBase = keyUrl.split('?')[0]
          const imgBase = img.src.split('?')[0]
          if (keyBase === imgBase) {
            base64Data = data
            break
          }
        }

        if (base64Data) {
          captures.push(base64Data)
        }
      }
    }

    return captures
  }

  /**
   * 画像URLとダウンロードURLを生成
   */
  private getImageUrls(dataUrl: string, filename: string): { imageUrl: string; downloadUrl: string } {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const fullFilename = `${filename}_${timestamp}.png`

    return {
      imageUrl: dataUrl,
      downloadUrl: fullFilename,
    }
  }

  /**
   * 利用中のブラウザがSafariであるか確認
   */
  private isSafari(): boolean {
    const ua = window.navigator.userAgent
    return /^((?!chrome|android).)*safari/i.test(ua)
  }

  /**
   * Safariブラウザ用に、キャプチャ画像のサイズを確認しながら正常なdataUrlを生成
   */
  private async buildDataUrlWithRetry(element: HTMLElement, minBlobSize = 90_000, maxAttempts = 10): Promise<string> {
    const FIXED_WIDTH = 1600
    const FIXED_HEIGHT = Math.round((FIXED_WIDTH * 9) / 14)
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const dataUrl = await domtoimage.toPng(element, {
        width: FIXED_WIDTH,
        height: FIXED_HEIGHT,
        bgcolor: 'white',
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
        },
      })
      const blob = await (await fetch(dataUrl)).blob()
      if (blob.size > minBlobSize) return dataUrl
    }
    return ''
  }
}
</script>

<style lang="stylus" scoped>
.study-individual
  // ヘッダーを除外
  height calc(100% - 32px)
  padding 0 10px

  &__content
    height 100%
    &__navigation
      padding 10px

    &__body
      display flex
      position relative
      // 100%からナビゲーション部の高さと余白10px分を引いた値
      height calc(100% - 74px)

      &__question
        margin 0 10px 0 0

  &[data-header-opened='true'] &__content
    &__body
      // ヘッダーオープン時はヘッダーの高さが増える
      height calc(100vh - 152px)

  &[data-screen-ratio='1'] &__content
    &__body
      &__question
        width 50%
      &__answer
        width 50%

  &[data-screen-ratio='1.5'] &__content
    &__body
      &__question
        width 60%
      &__answer
        width 40%

  &[data-screen-ratio='2'] &__content
    &__body
      &__question
        width 67%
      &__answer
        width 33%

  &[data-screen-ratio='3'] &__content
    &__body
      &__question
        width 75%
      &__answer
        width 25%

  &[data-vertical='true'] &__content
    &__body
      flex-direction row-reverse
      text-orientation upright

      &__question
        margin 0 0 0 10px

  // スクロールバーを非表示にしつつ、スクロール機能は維持
  &__content__body__question,
  &__content__body__answer
    // Webkit系ブラウザ (Chrome, Safari, Edge)
    &::-webkit-scrollbar
      width 0
      height 0
      display none

    // Firefox
    scrollbar-width none

    // IE/Edge legacy
    -ms-overflow-style none
</style>
